import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Stack,
  alpha,
  Button,
  Card,
} from "@mui/material"; // Added Card, Avatar, Button
import { motion } from "framer-motion";
import {
  Lightbulb,
  Eye,
  UsersRound,
  ShieldCheck,
  Settings,
  Recycle,
  ArrowRight, // Added for CTA button
  Target, // For Our Journey
  Award, // For Our Journey and Core Values
  TrendingUp, // For Our Journey and What We Offer
  CheckCircle, // For What We Offer
  Heart, // For Core Values
} from "lucide-react";

import Seo from "../components/Seo";

import Breadcrumb from "../components/Breadcrumb";
import { generateBreadcrumbs } from "../utils/breadcrumbs";

import { Link } from "react-router-dom";
import { useSelector } from "react-redux";

// Define theme colors for consistency and easier modification
const themeColors = {
  primary: "#F5B800", // Original brand color
  secondary: "#334155", // A deep, subtle grey for text
  background: "#f8fafd", // A very light, almost off-white background
  cardBackground: "#ffffff",
  shadow: "rgba(0, 0, 0, 0.08)", // Subtle shadow
  ctaBackground: "#2d3748", // A dark background for the CTA section
};

// Framer Motion Variants
const sectionVariant = {
  hidden: { opacity: 0, y: 50, scale: 0.98 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.8,
      ease: "easeInOut",
      when: "beforeChildren",
    },
  },
};

const itemVariant = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut",
    },
  },
};

// Icon style for the "Our Journey" section and "What We Offer"
const secondaryIconStyle = {
  width: 70,
  height: 70,
  borderRadius: "50%",
  backgroundColor: alpha(themeColors.primary, 0.1), // Lighter background for these icons
  boxShadow: `0 4px 12px ${alpha(themeColors.primary, 0.1)}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const AboutUsPage = () => {
  const cards = [
    {
      icon: <UsersRound />,
      title: "Connect Buyers & Sellers",
      description:
        "Seamlessly match verified buyers with trusted sellers, expanding your reach.",
    },
    {
      icon: <ShieldCheck />,
      title: "Ensure Transparency",
      description:
        "Clear pricing, verified listings, and secure transactions for peace of mind.",
    },
    {
      icon: <Recycle />,
      title: "Promote Sustainability",
      description:
        "Facilitating recycling efforts and reducing waste globally.",
    },
    {
      icon: <Settings />,
      title: "Trusted Technology",
      description: "Secure payment gateways and compliance-ready processe",
    },
  ];

  const timeLine = [
    {
      //year: "2019",
      title: "Eliminate Middlemen",
      icon: <Target size={32} color={themeColors.primary} />,
      text: "To cut out inefficiencies and give traders direct, fair access.",
    },
    {
      // year: "2020",
      title: "Bring Transparency",
      icon: <TrendingUp size={32} color={themeColors.primary} />,
      text: "To replace guesswork with real-time pricing and verified listings.",
    },
    {
      // year: "2022",
      title: "Drive Sustainability",
      icon: <Recycle size={32} color={themeColors.primary} />,
      text: "To support recycling and reduce waste through smarter trading.",
    },
    {
      //year: "2024",
      title: "Empower Businesses",
      icon: <Award size={32} color={themeColors.primary} />,
      text: "To provide scrap sellers and buyers with a reliable platform.",
    },
  ];

  const breadcrumbs = generateBreadcrumbs({ pageType: "aboutus" });

  const { token, role } = useSelector((state) => state.auth); // 👈 INSIDE component
  const isLoggedIn = Boolean(token);

  return (
    <>
      <Seo
        title="About ScrapTrade | Digital Future of Scrap Trading"
        description="ScrapTrade is transforming scrap like Uber changed rides. A user-friendly, transparent, and sustainable marketplace built on trust & innovation."
        url="https://scraptrade.com.au/about-us"
        canonical="https://scraptrade.com.au/about-us"
        image="https://scraptrade.com.au/assets/og/scrap.jpg"
        breadcrumbs={breadcrumbs}
        extraSchema={{
          "@context": "https://schema.org",
          "@type": "AboutPage",
          mainEntity: {
            "@type": "Organization",
            name: "ScrapTrade",
            url: "https://scraptrade.com.au",
          },
        }}
      />

      <Box sx={{ backgroundColor: themeColors.background, pt: 0 }}>
        {/* Hero Section */}
        <Box
          sx={{
            py: { xs: 5, md: 10 },
            backgroundColor: alpha(themeColors.primary, 0.05),
          }}
        >
          {" "}
          {/* Lighter background */}
          <Container maxWidth="lg">
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={7}>
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  variants={sectionVariant}
                  viewport={{ once: true, amount: 0.4 }}
                >
                  <Typography
                    variant="h2"
                    fontWeight={800}
                    gutterBottom
                    sx={{
                      fontSize: { xs: "2rem", md: "3.5rem" },
                      lineHeight: 1.2,
                    }}
                  >
                    Redefining{" "}
                    <Box component="span" sx={{ color: themeColors.primary }}>
                      The Future
                    </Box>{" "}
                    Scrap Trading
                  </Typography>

                  <Typography
                    variant="h5"
                    fontWeight={500}
                    gutterBottom
                    sx={{
                      fontSize: { xs: "1.5rem", md: "2rem" },
                      lineHeight: 1.4,
                      color: "text.secondary",
                    }}
                  >
                    ScrapTrade{" "}
                    <Box
                      component="span"
                      sx={{ color: themeColors.primary, fontWeight: 700 }}
                    >
                      - Scrap Marketplace
                    </Box>
                  </Typography>

                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ maxWidth: "700px", mt: 2 }}
                  >
                    We’re a digital scrap marketplace that connects recyclers,
                    industries, and sellers in real time. Making scrap trading
                    faster, safer, and more transparent than ever.
                  </Typography>
                </motion.div>
              </Grid>
              <Grid item xs={12} md={5}>
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  variants={itemVariant} // Using itemVariant for this card
                  viewport={{ once: true, amount: 0.3 }}
                >
                  <Card
                    sx={{
                      p: { xs: 3, md: 4 },
                      borderLeft: `5px solid ${themeColors.primary}`,
                      boxShadow: `0 8px 30px ${themeColors.shadow}`,
                      borderRadius: 3,
                    }}
                  >
                    <Typography
                      variant="h4"
                      fontWeight={700}
                      sx={{ color: themeColors.primary }}
                    >
                      20K+
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ color: alpha(themeColors.secondary, 0.9) }}
                    >
                      Verified Scrap Sellers
                    </Typography>
                    <Typography
                      variant="h4"
                      fontWeight={700}
                      mt={2}
                      sx={{ color: themeColors.primary }}
                    >
                      50+
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ color: alpha(themeColors.secondary, 0.9) }}
                    >
                      Cities We Serve Worldwide
                    </Typography>
                  </Card>
                </motion.div>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Our Mission & Vision */}
        <Container maxWidth="lg" sx={{ mt: 3, mb: 3 }}>
          <Breadcrumb items={breadcrumbs} />
        </Container>

        <Box
          sx={{
            position: "relative",
            py: { xs: 8, md: 12 },
            background: `radial-gradient(circle at top left, ${alpha(
              themeColors.primary,
              0.2,
            )}, ${themeColors.background})`,
            overflow: "hidden",
          }}
        >
          <Container maxWidth="lg">
            <motion.div
              initial="hidden"
              whileInView="visible"
              variants={sectionVariant}
              viewport={{ once: true, amount: 0.3 }}
            >
              <Typography
                variant="h3"
                fontWeight={700}
                textAlign="center"
                gutterBottom
                sx={{
                  fontSize: { xs: "2.5rem", md: "3.5rem" },
                  color: themeColors.secondary,
                  position: "relative",
                  "&::after": {
                    content: '""',
                    display: "block",
                    width: "60px",
                    height: "4px",
                    backgroundColor: themeColors.primary,
                    margin: "8px auto 0",
                    borderRadius: 2,
                  },
                }}
              >
                Our Mission & Vision
              </Typography>

              <Typography
                variant="h6"
                textAlign="center"
                color="text.secondary"
                mb={{ xs: 6, md: 10 }}
                sx={{
                  maxWidth: 700,
                  mx: "auto",
                  lineHeight: 1.7,
                  color: alpha(themeColors.secondary, 0.75),
                }}
              >
                The scrap industry has powered economies for centuries, but it’s
                been slowed down by outdated systems, inconsistent pricing, and
                endless middlemen. ScrapTrade was built to change that.
              </Typography>
            </motion.div>

            {/* Horizontal Mission & Vision container */}
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={4}
              justifyContent="center"
              alignItems="stretch"
            >
              {/* Mission Card */}
              <motion.div
                whileInView={{ opacity: 1, x: 0 }}
                initial={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.8 }}
                style={{ flex: 1 }}
              >
                <Box
                  sx={{
                    backdropFilter: "blur(6px)",
                    backgroundColor: alpha(themeColors.cardBackground, 0.9),
                    borderRadius: 4,
                    p: 4,
                    height: "100%",
                    boxShadow: `0 12px 30px ${alpha(themeColors.primary, 0.15)}`,
                    transition: "transform 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-5px)",
                      boxShadow: `0 16px 36px ${alpha(themeColors.primary, 0.3)}`,
                    },
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                    <Box
                      sx={{
                        backgroundColor: alpha(themeColors.primary, 0.1),
                        borderRadius: 2,
                        p: 1.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Lightbulb size={30} color={themeColors.primary} />
                    </Box>
                    <Typography
                      variant="h5"
                      fontWeight={700}
                      color={themeColors.primary}
                    >
                      Our Mission
                    </Typography>
                  </Stack>
                  <Typography
                    sx={{
                      color: alpha(themeColors.secondary, 0.9),
                      fontSize: "1.1rem",
                      lineHeight: 1.8,
                    }}
                  >
                    To empower scrap community and businesses with a platform
                    that delivers fair market value, verified transactions, and
                    eco-conscious practices; while driving growth for every
                    scrap player in the recycling chain.
                  </Typography>
                </Box>
              </motion.div>

              {/* Vision Card */}
              <motion.div
                whileInView={{ opacity: 1, x: 0 }}
                initial={{ opacity: 0, x: 50 }}
                transition={{ duration: 1 }}
                style={{ flex: 1 }}
              >
                <Box
                  sx={{
                    backdropFilter: "blur(6px)",
                    backgroundColor: alpha(themeColors.cardBackground, 0.9),
                    borderRadius: 4,
                    p: 4,
                    height: "100%",
                    boxShadow: `0 12px 30px ${alpha(themeColors.primary, 0.15)}`,
                    transition: "transform 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-5px)",
                      boxShadow: `0 16px 36px ${alpha(themeColors.primary, 0.3)}`,
                    },
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                    <Box
                      sx={{
                        backgroundColor: alpha(themeColors.primary, 0.1),
                        borderRadius: 2,
                        p: 1.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Eye size={30} color={themeColors.primary} />
                    </Box>
                    <Typography
                      variant="h5"
                      fontWeight={700}
                      color={themeColors.primary}
                    >
                      Our Vision
                    </Typography>
                  </Stack>
                  <Typography
                    sx={{
                      color: alpha(themeColors.secondary, 0.9),
                      fontSize: "1.1rem",
                      lineHeight: 1.8,
                    }}
                  >
                    To lead the global digital transformation of scrap trading,
                    creating an ecosystem that is efficient, sustainable, and
                    trusted by businesses worldwide.
                  </Typography>
                </Box>
              </motion.div>
            </Stack>
          </Container>

          {/* Decorative Glow */}
          <Box
            sx={{
              position: "absolute",
              top: "-100px",
              left: "-80px",
              width: 300,
              height: 300,
              background: themeColors.primary,
              opacity: 0.15,
              filter: "blur(120px)",
              borderRadius: "50%",
              zIndex: 0,
            }}
          />
        </Box>

        {/* About ScrapTrade */}
        <Box
          sx={{
            py: { xs: 5, md: 10 },
            backgroundColor: themeColors.cardBackground,
          }}
        >
          <Container maxWidth="lg">
            {/* Heading Section */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              variants={sectionVariant}
              viewport={{ once: true, amount: 0.3 }}
            >
              <Typography
                variant="h3"
                fontWeight={700}
                textAlign="center"
                gutterBottom
                sx={{
                  color: themeColors.secondary,
                  fontSize: { xs: "2.2rem", md: "3.2rem" },
                }}
              >
                About{" "}
                <Box component="span" sx={{ color: themeColors.primary }}>
                  ScrapTrade
                </Box>
              </Typography>
              <Typography
                variant="h6"
                textAlign="center"
                color="text.secondary"
                maxWidth="md"
                mx="auto"
                mb={6}
                sx={{
                  lineHeight: 1.6,
                  color: alpha(themeColors.secondary, 0.8),
                }}
              >
                Our platform makes scrap trading fast, transparent, and
                effortless, with real-time pricing, verified partners, and
                integrated logistics.
              </Typography>
            </motion.div>

            {/* Main Description Text */}
            {/* <Grid container justifyContent="center">
            <Grid item xs={12} md={8}>
              <motion.div
                initial="hidden"
                whileInView="visible"
                variants={sectionVariant}
                viewport={{ once: true, amount: 0.3 }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: "1.1rem",
                    lineHeight: 1.8,
                    textAlign: "center",
                    color: alpha(themeColors.secondary, 0.9),
                  }}
                  color="text.secondary"
                  mb={5}
                >
                 Built on a modern technology stack and refined by our in-house developers, ScrapTrade combines speed, security, and scalability. Backed by dedicated customer support, we ensure smooth experiences for every user, from first-time sellers to global recyclers.
                  <br />
                  <br />
                  ScrapTrade isn’t just a marketplace. It’s the future of scrap trading worldwide.
                </Typography>
              </motion.div>
            </Grid>
          </Grid>  */}

            <Grid container spacing={6} justifyContent="center">
              {/* Paragraph 1 - Left */}

              <Grid item xs={12} md={8}>
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  variants={sectionVariant}
                  viewport={{ once: true, amount: 0.3 }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: "1.15rem",
                      lineHeight: 1.9,
                      textAlign: { xs: "center", md: "left" },
                      color: alpha(themeColors.secondary, 0.9),
                    }}
                  >
                    <strong>ScrapTrade</strong> is owned and operated by{" "}
                    <strong>
                      MOBEIUS TECHNOLOGIES PTY LTD (ABN: 49 693 656 932 | ACN:
                      693 656 932)
                    </strong>
                    , a fully registered and compliant Australian company. As a
                    trusted platform in the scrap trading industry, we adhere to
                    all applicable Australian laws and regulations to ensure a
                    safe, transparent, and accessible marketplace for buyers,
                    sellers, and recycling professionals nationwide.
                    {/* <strong>ScrapTrade</strong> is a digital marketplace for
                    scrap trading, built to make the industry faster, smarter,
                    and more transparent. Think of it as{" "}
                    <Box
                      component="span"
                      sx={{ color: themeColors.primary, fontWeight: 600 }}
                    >
                      the world of scrap
                    </Box>
                    , a platform that connects scrap buyers and scrap sellers
                    instantly, with fair pricing and trusted logistics. */}
                  </Typography>
                </motion.div>
              </Grid>

              <Grid item xs={12} md={8}>
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  variants={sectionVariant}
                  viewport={{ once: true, amount: 0.3 }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: "1.15rem",
                      lineHeight: 1.9,
                      textAlign: { xs: "center", md: "left" },
                      color: alpha(themeColors.secondary, 0.9),
                    }}
                  >
                    <strong>ScrapTrade</strong> is a digital marketplace for
                    scrap trading, built to make the industry faster, smarter,
                    and more transparent. Think of it as{" "}
                    <Box
                      component="span"
                      sx={{ color: themeColors.primary, fontWeight: 600 }}
                    >
                      the world of scrap
                    </Box>
                    , a platform that connects scrap buyers and scrap sellers
                    instantly, with fair pricing and trusted logistics.
                  </Typography>
                </motion.div>
              </Grid>

              {/* Paragraph 2 - Right */}
              <Grid item xs={12} md={8}>
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  variants={sectionVariant}
                  viewport={{ once: true, amount: 0.3 }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: "1.15rem",
                      lineHeight: 1.9,
                      textAlign: { xs: "center", md: "left" },
                      color: alpha(themeColors.secondary, 0.9),
                    }}
                  >
                    Designed to be user-friendly for everyone, ScrapTrade
                    simplifies listings, bidding, and payments while ensuring
                    security through{" "}
                    <Box
                      component="span"
                      sx={{ color: themeColors.primary, fontWeight: 600 }}
                    >
                      smart logistics tracking via trusted partners and market
                      leaders, KYC approved buyers and sellers, and secure
                      payment processing
                    </Box>{" "}
                    and{" "}
                    <Box
                      component="span"
                      sx={{ color: themeColors.primary, fontWeight: 600 }}
                    >
                      quality assurance checks
                    </Box>
                    . Behind the platform is an in-house team of developers and
                    support specialists dedicated to delivering speed,
                    reliability, and seamless service.
                  </Typography>
                </motion.div>
              </Grid>

              {/* Paragraph 3 - Left */}
              <Grid item xs={12} md={8} sx={{ mb: 6 }}>
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  variants={sectionVariant}
                  viewport={{ once: true, amount: 0.3 }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: "1.15rem",
                      lineHeight: 1.9,
                      textAlign: { xs: "center", md: "left" },
                      color: alpha(themeColors.secondary, 0.9),
                    }}
                  >
                    More than just a trading platform, ScrapTrade is driving{" "}
                    <Box
                      component="span"
                      sx={{ color: themeColors.primary, fontWeight: 600 }}
                    >
                      sustainability worldwide
                    </Box>{" "}
                    by reducing waste and powering the circular economy. It’s
                    not only about trading scrap. It’s about{" "}
                    <Box
                      component="span"
                      sx={{ color: themeColors.primary, fontWeight: 600 }}
                    >
                      building the future of recycling
                    </Box>
                    .
                  </Typography>
                </motion.div>
              </Grid>
            </Grid>

            {/* Feature Cards in Centered Grid */}
            <Grid
              container
              spacing={4}
              justifyContent="center"
              alignItems="center"
            >
              {cards.map((card, index) => (
                <Grid
                  item
                  key={index}
                  sx={{
                    width: 500,
                    height: 150,
                    display: "flex",
                  }}
                >
                  <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.5, delay: index * 0.15 }}
                    style={{ width: "100%" }}
                  >
                    <Card
                      elevation={0}
                      sx={{
                        p: 3,
                        backgroundColor: "#f9fbfd",
                        borderRadius: 3,
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        transition: "all 0.4s ease-in-out",
                        transform: "scale(1)",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                        "&:hover": {
                          transform: "scale(1.03)",
                          boxShadow: `0 0 20px 4px ${themeColors.primary}33, 0 8px 30px rgba(0,0,0,0.1)`, // uses theme primary with alpha
                          background:
                            "linear-gradient(135deg, #f9fbfd 0%, #e3f2fd 100%)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          flexShrink: 0,
                          borderRadius: "50%",
                          backgroundColor: "rgba(255, 193, 7, 0.1)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transition: "background-color 0.3s ease",
                          "&:hover": {
                            backgroundColor: "rgba(255, 193, 7, 0.25)",
                          },
                        }}
                      >
                        {card.icon}
                      </Box>

                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="h6"
                          fontWeight={700}
                          gutterBottom
                          noWrap
                        >
                          {card.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {card.description}
                        </Typography>
                      </Box>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Our Journey */}
        <Box
          sx={{
            backgroundColor: themeColors.background,
            py: { xs: 5, md: 10 },
          }}
        >
          <Container maxWidth="lg">
            {/* Header */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              variants={sectionVariant}
              viewport={{ once: true, amount: 0.3 }}
            >
              <Typography
                variant="h3"
                fontWeight={700}
                textAlign="center"
                gutterBottom
                sx={{
                  color: themeColors.secondary,
                  fontSize: { xs: "2rem", md: "3rem" },
                }}
              >
                Why We Started{" "}
                <Box component="span" sx={{ color: themeColors.primary }}>
                  Scrap Trade
                </Box>
              </Typography>
              <Typography
                variant="h6"
                textAlign="center"
                sx={{
                  color: alpha(themeColors.secondary, 0.8),
                  mb: { xs: 6, md: 8 },
                  lineHeight: 1.6,
                }}
              >
                Our journey began with a simple idea: scrap trading should be
                smarter, faster, and fairer.
              </Typography>
            </motion.div>

            {/* Timeline */}
            <Box
              sx={{
                position: "relative",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  left: "50%",
                  top: 0,
                  bottom: 0,
                  width: "4px",
                  backgroundColor: alpha(themeColors.primary, 0.2),
                  transform: "translateX(-50%)",
                  borderRadius: 2,
                },
              }}
            >
              {timeLine.map((item, index) => {
                const isLeft = index % 2 === 0;

                return (
                  <motion.div
                    key={index}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.3 }}
                    variants={itemVariant}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        position: "relative",
                        mb: { xs: 5, md: 6 },
                        flexDirection: { xs: "column", md: "row" }, // stack on small
                        alignItems: "center",
                      }}
                    >
                      {/* Neon Glow Capsule Card */}
                      <Box
                        sx={{
                          position: "relative",
                          width: { xs: "90%", md: "40%" }, // smaller width on mobile for padding
                          px: 3,
                          py: 2.5,
                          borderRadius: "999px",
                          background: `linear-gradient(
                135deg,
                ${alpha("#ffffff", 0.1)},
                ${alpha("#ffffff", 0.02)}
              )`,
                          backdropFilter: "blur(12px)",
                          border: `1px solid ${alpha(themeColors.primary, 0.15)}`,
                          boxShadow: `0 4px 20px ${alpha(
                            themeColors.primary,
                            0.6,
                          )}`,
                          transition: "all 0.3s ease-in-out",
                          "&:hover": {
                            boxShadow: `0 0 24px ${alpha(
                              themeColors.primary,
                              0.9,
                            )}`,
                            transform: "scale(1.025)",
                          },

                          // Desktop: alternate left/right
                          // Mobile: always centered
                          ml: { xs: "auto", md: isLeft ? 10 : "auto" },
                          mr: { xs: "auto", md: isLeft ? "auto" : 10 },
                          textAlign: { xs: "center", md: "left" },
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          fontWeight={600}
                          sx={{
                            color: themeColors.primary,
                            mb: 0.5,
                            fontSize: "0.9rem",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: alpha(themeColors.secondary, 0.85),
                            fontSize: "0.85rem",
                            lineHeight: 1.5,
                          }}
                        >
                          {item.text}
                        </Typography>
                      </Box>

                      {/* Glowing Icon Dot */}
                      <Box
                        sx={{
                          position: "absolute",
                          top: { xs: "0", md: "50%" }, // mobile: place dot above card
                          transform: {
                            xs: "translate(-50%, -50%)",
                            md: "translate(-50%, -50%)",
                          },
                          left: "50%",
                          width: { xs: 12, md: 20 }, // smaller on mobile
                          height: { xs: 12, md: 20 },
                          borderRadius: "50%",
                          backgroundColor: themeColors.primary,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#fff",
                          fontSize: "0.75rem",
                          boxShadow: `0 0 8px ${alpha(themeColors.primary, 0.4)}`,
                          zIndex: 2,
                        }}
                      >
                        {item.icon}
                      </Box>
                    </Box>
                  </motion.div>
                );
              })}
            </Box>
          </Container>
        </Box>

        {/* What We Offer */}
        <Box
          sx={{
            backgroundColor: alpha(themeColors.primary, 0.05),
            py: { xs: 5, md: 10 },
          }}
        >
          {" "}
          {/* Lighter background */}
          <Container maxWidth="lg">
            <motion.div
              initial="hidden"
              whileInView="visible"
              variants={sectionVariant}
              viewport={{ once: true, amount: 0.3 }}
            >
              <Typography
                variant="h3"
                fontWeight={700}
                textAlign="center"
                gutterBottom
                sx={{
                  color: themeColors.secondary,
                  fontSize: { xs: "2rem", md: "3rem" },
                }}
              >
                What We{" "}
                <Box component="span" sx={{ color: themeColors.primary }}>
                  Offer
                </Box>
              </Typography>
              <Typography
                variant="h6"
                textAlign="center"
                color="text.secondary"
                mb={{ xs: 6, md: 8 }}
                sx={{
                  lineHeight: 1.6,
                  color: alpha(themeColors.secondary, 0.8),
                }}
              >
                Scrap Trade delivers a smarter, safer, and more transparent way
                to buy, sell, and move scrap, powered by latest technology and
                built for the future.
              </Typography>
            </motion.div>
            <Grid container spacing={4} justifyContent="center">
              {[
                {
                  icon: <ShieldCheck size={32} color={themeColors.primary} />,
                  title: "Verified Sellers",
                },
                {
                  icon: <TrendingUp size={32} color={themeColors.primary} />,
                  title: "Live Market Pricing",
                },
                {
                  icon: <Recycle size={32} color={themeColors.primary} />,
                  title: "Automation",
                },
                {
                  icon: <CheckCircle size={32} color={themeColors.primary} />,
                  title: "Safe Transactions",
                },
              ].map((item, i) => (
                <Grid item xs={12} md={3} key={i}>
                  <motion.div
                    initial="hidden"
                    whileInView="visible"
                    variants={itemVariant} // Using itemVariant for these cards
                    viewport={{ once: true, amount: 0.3 }}
                  >
                    <Card
                      sx={{
                        textAlign: "center",
                        py: 4,
                        px: 2,
                        width: 220, // keeps width compact
                        margin: "0 auto", // centers the card
                        boxShadow: `0 8px 30px ${themeColors.shadow}`,
                        borderRadius: 3,
                        height: "100%",
                        transition: "transform 0.3s ease-in-out",
                        "&:hover": {
                          transform: "translateY(-10px)",
                        },
                      }}
                    >
                      <Box sx={{ ...secondaryIconStyle, mx: "auto", mb: 2 }}>
                        {item.icon}
                      </Box>
                      <Typography
                        variant="h6"
                        fontWeight={600}
                        sx={{ color: themeColors.secondary }}
                      >
                        {item.title}
                      </Typography>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Core Values */}
        <Box
          sx={{
            backgroundColor: themeColors.cardBackground,
            py: { xs: 5, md: 10 },
          }}
        >
          {" "}
          {/* Changed background */}
          <Container maxWidth="lg">
            <motion.div
              initial="hidden"
              whileInView="visible"
              variants={sectionVariant}
              viewport={{ once: true, amount: 0.3 }}
            >
              <Typography
                variant="h3"
                fontWeight={700}
                textAlign="center"
                gutterBottom
                sx={{
                  color: themeColors.secondary,
                  fontSize: { xs: "2rem", md: "3rem" },
                }}
              >
                Our Core{" "}
                <Box component="span" sx={{ color: themeColors.primary }}>
                  Values
                </Box>
              </Typography>
              <Typography
                variant="h6"
                textAlign="center"
                color="text.secondary"
                mb={{ xs: 6, md: 8 }}
                sx={{
                  lineHeight: 1.6,
                  color: alpha(themeColors.secondary, 0.8),
                }}
              >
                The principles that drive ScrapTrade forward.
              </Typography>
            </motion.div>
            <Grid container spacing={4} justifyContent="center">
              {[
                {
                  icon: <Heart size={32} color={themeColors.primary} />,
                  title: "Integrity",
                  desc: "Every trade is built on honesty, transparency, and accountability.",
                },
                {
                  icon: <Target size={32} color={themeColors.primary} />,
                  title: "Efficiency",
                  desc: "We cut through complexity with smart, streamlined systems.",
                },
                {
                  icon: <Award size={32} color={themeColors.primary} />,
                  title: "Excellence",
                  desc: "Relentlessly raising the bar for digital scrap trading solutions.",
                },
                {
                  icon: <Award size={32} color={themeColors.primary} />,
                  title: "Sustainability",
                  desc: "Turning waste into opportunity and driving a cleaner future.",
                },
              ].map((item, i) => (
                <Grid item xs={12} md={4} key={i}>
                  <motion.div
                    initial="hidden"
                    whileInView="visible"
                    variants={itemVariant} // Using itemVariant for these cards
                    viewport={{ once: true, amount: 0.3 }}
                  >
                    <Card
                      sx={{
                        textAlign: "center",
                        p: 4,
                        boxShadow: `0 8px 30px ${themeColors.shadow}`, // Consistent shadow
                        borderRadius: 3,
                        height: "100%",
                        transition: "transform 0.3s ease-in-out",
                        "&:hover": {
                          transform: "translateY(-10px)",
                        },
                      }}
                    >
                      <Box sx={{ ...secondaryIconStyle, mx: "auto", mb: 2 }}>
                        {item.icon}
                      </Box>
                      <Typography
                        variant="h6"
                        fontWeight={700}
                        mt={2}
                        sx={{ color: themeColors.secondary }}
                      >
                        {item.title}
                      </Typography>
                      <Typography
                        color="text.secondary"
                        mt={1}
                        sx={{ color: alpha(themeColors.secondary, 0.9) }}
                      >
                        {item.desc}
                      </Typography>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Call to Action Section */}
        <Box
          sx={{
            backgroundColor: themeColors.ctaBackground,
            py: { xs: 5, md: 10 },
            textAlign: "center",
          }}
        >
          <Container maxWidth="md">
            <motion.div
              initial="hidden"
              whileInView="visible"
              variants={sectionVariant}
              viewport={{ once: true, amount: 0.4 }}
            >
              <Typography
                variant="h3"
                fontWeight={700}
                gutterBottom
                sx={{
                  color: themeColors.cardBackground,
                  fontSize: { xs: "2rem", md: "3rem" },
                }} // White text for contrast
              >
                Ready to{" "}
                <Box component="span" sx={{ color: themeColors.primary }}>
                  Join
                </Box>{" "}
                ScrapTrade?
              </Typography>
              <Typography
                variant="h6"
                color="text.secondary"
                maxWidth="sm"
                mx="auto"
                mb={4}
                sx={{
                  lineHeight: 1.6,
                  color: alpha(themeColors.cardBackground, 0.8),
                }} // Softer white
              >
                Experience seamless scrap trading, enhanced transparency, and
                sustainable growth. Sign up today and be part of the world’s
                leading digital scrap ecosystem.
              </Typography>
              <Button
                component={Link}
                to={
                  isLoggedIn
                    ? role === "seller"
                      ? "/seller/dashboard"
                      : "/dashboard"
                    : "/register"
                }
                variant="contained"
                size="large"
                sx={{
                  backgroundColor: themeColors.primary,
                  color: themeColors.cardBackground,
                  fontWeight: 700,
                  py: 1.5,
                  px: 4,
                  borderRadius: 3,
                  boxShadow: `0 8px 24px ${alpha(themeColors.primary, 0.4)}`,
                  "&:hover": {
                    backgroundColor: alpha(themeColors.primary, 0.9),
                    boxShadow: `0 12px 30px ${alpha(themeColors.primary, 0.6)}`,
                    transform: "translateY(-3px)",
                  },
                  transition: "all 0.3s ease-in-out",
                }}
                endIcon={<ArrowRight />}
              >
                Get Started Now
              </Button>
            </motion.div>
          </Container>
        </Box>
      </Box>
    </>
  );
};

export default AboutUsPage;

{
  /* Leadership Team */
}
{
  /* <Box
        sx={{ backgroundColor: themeColors.background, py: { xs: 5, md: 10 } }}
      >
        {" "}
        <Container maxWidth="lg">
          <motion.div
            initial="hidden"
            whileInView="visible"
            variants={sectionVariant}
            viewport={{ once: true, amount: 0.3 }}
          >
            <Typography
              variant="h3"
              fontWeight={700}
              textAlign="center"
              gutterBottom
              sx={{
                color: themeColors.secondary,
                fontSize: { xs: "2rem", md: "3rem" },
              }}
            >
              Our{" "}
              <Box component="span" sx={{ color: themeColors.primary }}>
                Leadership
              </Box>
            </Typography>
            <Typography
              variant="h6"
              textAlign="center"
              color="text.secondary"
              mb={{ xs: 6, md: 8 }}
              sx={{ lineHeight: 1.6, color: alpha(themeColors.secondary, 0.8) }}
            >
              Meet the people driving innovation at ScrapTrade.
            </Typography>
          </motion.div>
          <Grid container spacing={4} justifyContent="center">
            {[
              {
                name: "Jane Smith",
                role: "CEO & Founder",
                avatar: "https://placehold.co/100x100/F5B800/FFFFFF?text=JS",
              },
              {
                name: "John Doe",
                role: "CTO",
                avatar: "https://placehold.co/100x100/334155/FFFFFF?text=JD",
              },
              {
                name: "Maria Lopez",
                role: "Head of Operations",
                avatar: "https://placehold.co/100x100/F5B800/FFFFFF?text=ML",
              },
            ].map((member, i) => (
              <Grid item xs={12} sm={4} key={i}>
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  variants={itemVariant} // Using itemVariant for these cards
                  viewport={{ once: true, amount: 0.3 }}
                >
                  <Card
                    sx={{
                      p: 3,
                      textAlign: "center",
                      boxShadow: `0 8px 30px ${themeColors.shadow}`, // Consistent shadow
                      borderRadius: 3,
                      height: "100%",
                      transition: "transform 0.3s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-10px)",
                      },
                    }}
                  >
                    <Avatar
                      src={member.avatar}
                      alt={member.name}
                      sx={{
                        width: 80,
                        height: 80,
                        mx: "auto",
                        mb: 2,
                        border: `3px solid ${themeColors.primary}`,
                      }}
                    />
                    <Typography
                      variant="h6"
                      fontWeight={600}
                      sx={{ color: themeColors.secondary }}
                    >
                      {member.name}
                    </Typography>
                    <Typography
                      color="text.secondary"
                      sx={{ color: alpha(themeColors.secondary, 0.9) }}
                    >
                      {member.role}
                    </Typography>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box> */
}
